import { EdgeProps, Node } from '@xyflow/react';
import {
	SVGDrawFunction,
	svgDrawSmoothLinePath,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/functions/drawSvgPath.ts';
import {
	pathfindingAStarDiagonal,
	PathFindingFunction,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/functions/generatePath.ts';
import { toInteger } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/functions/utils.ts';
import { getBoundingBoxes } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/functions/getBoundingBoxes.ts';
import {
	createGrid,
	PointInfo,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/functions/createGrid.ts';
import { gridToGraphPoint } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/functions/pointConversion.ts';

export type EdgeParams = Pick<
	EdgeProps,
	'sourceX' | 'sourceY' | 'targetX' | 'targetY' | 'sourcePosition' | 'targetPosition'
>;

export type GetSmartEdgeOptions = {
	gridRatio?: number;
	nodeXPadding?: number;
	nodeYPadding?: number;
	drawEdge?: SVGDrawFunction;
	generatePath?: PathFindingFunction;
	withConnectorPadding?: boolean;
};

export type GetSmartEdgeParams<NodeDataType extends Node = Node> = EdgeParams & {
	options?: GetSmartEdgeOptions;
	nodes: Node<NodeDataType>[];
};

export type GetSmartEdgeReturn = {
	svgPathString: string;
	edgeCenterX: number;
	edgeCenterY: number;
};

export const getSmartEdge = <NodeDataType extends Node = Node>({
	options = {
		withConnectorPadding: true,
	},
	nodes = [],
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
}: GetSmartEdgeParams<NodeDataType>): GetSmartEdgeReturn | null => {
	try {
		const { drawEdge = svgDrawSmoothLinePath, generatePath = pathfindingAStarDiagonal } =
			options;

		let { gridRatio = 10, nodeXPadding = 10, nodeYPadding = 10 } = options;
		gridRatio = toInteger(gridRatio);
		nodeXPadding = toInteger(nodeXPadding, -100);
		nodeYPadding = toInteger(nodeYPadding, -100);

		// We use the node's information to generate bounding boxes for them
		// and the graph
		const { graphBox, nodeBoxes } = getBoundingBoxes<NodeDataType>(
			nodes,
			nodeXPadding,
			nodeYPadding,
			gridRatio
		);

		const source: PointInfo = {
			x: sourceX,
			y: sourceY + (options?.withConnectorPadding ? 10 : 0),
			position: sourcePosition,
		};

		const target: PointInfo = {
			x: targetX,
			y: targetY - (options?.withConnectorPadding ? 10 : 0),
			position: targetPosition,
		};

		// With this information, we can create a 2D grid representation of
		// our graph, that tells us where in the graph there is a "free" space or not
		const { grid, start, end } = createGrid(graphBox, nodeBoxes, source, target, gridRatio);

		// We then can use the grid representation to do pathfinding
		const generatePathResult = generatePath(grid, start, end);

		if (generatePathResult === null) {
			return null;
		}

		const { fullPath, smoothedPath } = generatePathResult;

		// Here we convert the grid path to a sequence of graph coordinates.
		const graphPath = smoothedPath.map((gridPoint) => {
			const [x, y] = gridPoint;
			const graphPoint = gridToGraphPoint({ x, y }, graphBox.xMin, graphBox.yMin, gridRatio);
			return [graphPoint.x, graphPoint.y];
		});

		// Finally, we can use the graph path to draw the edge
		const svgPathString = drawEdge(source, target, graphPath);

		// Compute the edge's middle point using the full path, so users can use
		// it to position their custom labels
		const index = Math.floor(fullPath.length / 2);
		const middlePoint = fullPath[index];
		const [middleX, middleY] = middlePoint;
		const { x: edgeCenterX, y: edgeCenterY } = gridToGraphPoint(
			{ x: middleX, y: middleY },
			graphBox.xMin,
			graphBox.yMin,
			gridRatio
		);

		return { svgPathString, edgeCenterX, edgeCenterY };
	} catch {
		return null;
	}
};
